import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Table, Typography, Button, message, Modal, Select, Input, Space, Tooltip, Spin } from "antd";
import { UserAddOutlined, EditOutlined } from "@ant-design/icons";
import axios from "axios";
import { useTranslation } from "react-i18next";
import useToken from "../Authentication/useToken";
import Constants from "../Utils/Constants";
import CreatePatient from "../Patient/CreatePatient";
import PatientDetails from "../Patient/PatientDetails";
import { getPatientAction } from "./EligibilityRequestList.helpers";

const { Title } = Typography;

const statusMeta = (status, t = (_k, fallback) => fallback) => {
  if (status === "accepted") {
    return {
      label: t("elig_status_accepted", "Acceptée"),
      color: "#389e0d",
      chipBg: "#dcfce7",
      chipText: "#166534",
    };
  }
  if (status === "in_progress") {
    return {
      label: t("elig_status_pending", "En attente"),
      color: "#1677ff",
      chipBg: "#dbeafe",
      chipText: "#1677ff",
    };
  }
  if (status === "rejected") {
    return {
      label: t("elig_status_rejected_returned", "Refusée / retournée"),
      color: "#cf1322",
      chipBg: "#fee2e2",
      chipText: "#b91c1c",
    };
  }
  return {
    label: t("elig_status_pending", "En attente"),
    color: "#1677ff",
    chipBg: "#dbeafe",
    chipText: "#1677ff",
  };
};
const parseStoredRoles = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem("userRoles") || "[]");
    if (Array.isArray(parsed)) {
      return parsed.map((role) => String(role).toLowerCase());
    }
  } catch (_error) {
    // Ignore invalid local JSON and use fallback role.
  }
  const fallbackRole = String(localStorage.getItem("userRole") || "").toLowerCase();
  return fallbackRole ? [fallbackRole] : [];
};

const containerStyle = {
  width: "100%",
  maxWidth: "100%",
  margin: "0 auto",
  padding: 24,
  boxSizing: "border-box",
};

const headerCardStyle = {
  border: "1px solid #d9e8ff",
  borderRadius: 16,
  padding: 20,
  marginBottom: 16,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: 16,
  background: "#fafcff",
};

const chipsStyle = { display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 };

const chipStyle = (bg, color = "#1f2937") => ({
  borderRadius: 999,
  padding: "4px 10px",
  fontSize: 12,
  fontWeight: 600,
  background: bg,
  color,
});

const statusTextStyle = (status) => ({
  color: statusMeta(status).color,
  fontWeight: 600,
});

const formatRequestId = (requestId) =>
  Number(requestId) > 0 ? `REQ-${String(requestId).padStart(6, "0")}` : "";

const EligibilityRequestList = ({ viewMode }) => {
  const { t } = useTranslation(["Patients", "Backend"]);
  const { token } = useToken();
  const tRef = useRef(t);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assignModal, setAssignModal] = useState({
    open: false,
    dispatchId: null,
    loading: false,
    optionsLoading: false,
    kinesiologists: [],
    selectedKinesiologistId: null,
  });
  const [rejectModal, setRejectModal] = useState({
    open: false,
    assignmentId: null,
    reason: "",
    loading: false,
  });
  const [createPatientModal, setCreatePatientModal] = useState({
    open: false,
    assignmentId: null,
    requestId: null,
    initialValues: null,
  });
  const [editPatientModal, setEditPatientModal] = useState({
    open: false,
    loading: false,
    patient: null,
    requestId: null,
  });

  const roles = useMemo(parseStoredRoles, []);
  const hasManagerRole = roles.includes("clinic_manager");
  const hasKinesiologistRole = roles.includes("kinesiologist");
  const hasAdminRole = roles.includes("admin") || roles.includes("superadmin");

  const requestedView = String(viewMode || "").toLowerCase();
  const effectiveView = (() => {
    if (requestedView === "manager" && (hasManagerRole || hasAdminRole)) return "manager";
    if (requestedView === "kinesiologist" && (hasKinesiologistRole || hasAdminRole)) {
      return "kinesiologist";
    }
    if (hasManagerRole && !hasKinesiologistRole) return "manager";
    if (hasKinesiologistRole) return "kinesiologist";
    if (hasManagerRole) return "manager";
    if (hasAdminRole) return requestedView === "manager" ? "manager" : "kinesiologist";
    return "kinesiologist";
  })();

  const isManagerView = effectiveView === "manager";
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${Constants.SERVER_URL}/eligibility-request`, {
        params: { view: effectiveView },
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Erreur chargement des demandes :", error);
      message.error(
        tRef.current("elig_error_loading_requests", "Erreur lors du chargement des demandes.")
      );
    } finally {
      setLoading(false);
    }
  }, [effectiveView, token]);

  useEffect(() => {
    if (!token) return;
    fetchRequests();
  }, [token, effectiveView, fetchRequests]);

  const summary = useMemo(() => {
    const base = { total: rows.length, pending: 0, accepted: 0, rejected: 0 };
    rows.forEach((row) => {
      const status = String(row?.status || "").toLowerCase();
      if (status === "accepted") base.accepted += 1;
      else if (status === "in_progress") base.pending += 1;
      else if (status === "rejected") base.rejected += 1;
      else base.pending += 1;
    });
    return base;
  }, [rows]);

  const openAssignModal = async (dispatchId) => {
    setAssignModal({
      open: true,
      dispatchId,
      loading: false,
      optionsLoading: true,
      kinesiologists: [],
      selectedKinesiologistId: null,
    });

    try {
      const res = await axios.get(
        `${Constants.SERVER_URL}/eligibility-request/dispatches/${dispatchId}/kinesiologists`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const options = (res.data || []).map((kinesiologist) => ({
        value: kinesiologist.idKinesiologist,
        label: `${kinesiologist.firstname || ""} ${kinesiologist.lastname || ""}`.trim(),
      }));
      setAssignModal((prev) => ({
        ...prev,
        optionsLoading: false,
        kinesiologists: options,
      }));
    } catch (error) {
      console.error(error);
      message.error(
        t("elig_error_load_assignable_kinesiologists", "Impossible de charger les kinésiologues assignables.")
      );
      setAssignModal((prev) => ({ ...prev, optionsLoading: false }));
    }
  };

  const closeAssignModal = () => {
    setAssignModal({
      open: false,
      dispatchId: null,
      loading: false,
      optionsLoading: false,
      kinesiologists: [],
      selectedKinesiologistId: null,
    });
  };

  const confirmAssign = async (forceReassign = false) => {
    const shouldForceReassign = forceReassign === true;
    if (!assignModal.dispatchId || !assignModal.selectedKinesiologistId) {
      message.warning(t("elig_warning_select_kinesiologist", "Veuillez sélectionner un kinésiologue."));
      return;
    }

    try {
      setAssignModal((prev) => ({ ...prev, loading: true }));
      await axios.post(
        `${Constants.SERVER_URL}/eligibility-request/dispatches/${assignModal.dispatchId}/assign`,
        {
          kinesiologistId: assignModal.selectedKinesiologistId,
          forceReassign: shouldForceReassign,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success(t("elig_success_assigned", "Demande assignée avec succès."));
      closeAssignModal();
      fetchRequests();
    } catch (error) {
      console.error(error);
      const backendMessage = error?.response?.data?.message;
      const requiresForce = error?.response?.data?.requiresForce === true;
      if (
        (backendMessage === "kinesiologist_previously_refused_request" || requiresForce) &&
        !shouldForceReassign
      ) {
        const selectedKinesiologist = assignModal.kinesiologists.find(
          (kinesiologist) =>
            Number(kinesiologist.value) === Number(assignModal.selectedKinesiologistId)
        );
        const kinesiologistLabel =
          selectedKinesiologist?.label || t("elig_label_this_kinesiologist", "ce kinésiologue");
        const previousRejectedReason = String(
          error?.response?.data?.previousRejectedReason || ""
        ).trim();

        setAssignModal((prev) => ({ ...prev, loading: false }));
        const previousReasonText = previousRejectedReason
          ? ` ${t("elig_force_modal_previous_reason", {
              reason: previousRejectedReason,
              defaultValue: "Motif précédent : {{reason}}.",
            })}`
          : "";
        Modal.confirm({
          title: t("elig_force_modal_title", "Ce kinésiologue a déjà refusé cette requête"),
          content: `${t("elig_force_modal_content", {
            name: kinesiologistLabel,
            defaultValue: "{{name}} a déjà refusé cette demande.",
          })}${previousReasonText} ${t(
            "elig_force_modal_choose_or_force",
            "Vous pouvez choisir un autre kinésiologue ou forcer la réassignation."
          )}`,
          okText: t("elig_force_modal_ok", "Forcer quand même"),
          cancelText: t("elig_force_modal_cancel", "Choisir un autre"),
          onOk: async () => {
            await confirmAssign(true);
          },
        });
        return;
      }
      const statusCode = error?.response?.status;
      const fallbackMessage =
        error?.response?.data?.message ||
        error?.message ||
        t("elig_error_assign_default", "Impossible d'assigner la demande.");
      if (backendMessage === "forbidden_clinic_manager_or_admin_only") {
        message.error(
          t(
            "elig_error_forbidden_assign",
            "Votre profil n'a pas les droits pour assigner cette demande."
          )
        );
      } else if (backendMessage === "forbidden_clinic_scope") {
        message.error(
          t(
            "elig_error_forbidden_scope",
            "Vous ne pouvez pas assigner une demande hors de votre clinique."
          )
        );
      } else {
        message.error(
          statusCode ? `${fallbackMessage} (code ${statusCode})` : fallbackMessage
        );
      }
      setAssignModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const cancelManagerAssignment = async (assignmentId) => {
    if (!assignmentId) return;

    Modal.confirm({
      title: t("elig_confirm_cancel_assignment_title", "Annuler cette assignation ?"),
      content: t(
        "elig_confirm_cancel_assignment_content",
        "Le kinésiologue ne verra plus cette assignation en attente. Vous pourrez ensuite réassigner la demande."
      ),
      okText: t("elig_confirm_cancel_assignment_ok", "Annuler l'assignation"),
      cancelText: t("button_close", { ns: "Patients", defaultValue: "Fermer" }),
      onOk: async () => {
        try {
          await axios.patch(
            `${Constants.SERVER_URL}/eligibility-request/assignments/${assignmentId}/cancel`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          message.success(t("elig_success_assignment_cancelled", "Assignation annulée."));
          fetchRequests();
        } catch (error) {
          console.error(error);
          message.error(
            error?.response?.data?.message ||
              t("elig_error_cancel_assignment", "Impossible d'annuler l'assignation.")
          );
        }
      },
    });
  };

  const acceptAssignment = async (assignmentId) => {
    try {
      await axios.patch(
        `${Constants.SERVER_URL}/eligibility-request/assignments/${assignmentId}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success(t("elig_success_assignment_accepted", "Demande acceptée."));
      fetchRequests();
    } catch (error) {
      console.error(error);
      message.error(
        error?.response?.data?.message ||
          t("elig_error_accept_assignment", "Erreur lors de l'acceptation.")
      );
    }
  };

  const openCreatePatientModal = (record) => {
    const maybeEmail = String(record?.email || "").trim();
    const validEmail = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(maybeEmail)
      ? maybeEmail
      : "";
    setCreatePatientModal({
      open: true,
      assignmentId: record?.assignmentId || null,
      requestId: record?.requestId || null,
      initialValues: {
        patientFirstName: String(record?.patientFirstname || "").trim(),
        patientLastName: String(record?.patientLastname || "").trim(),
        patientPhone: String(record?.phoneNumber || "").trim(),
        patientEmail: validEmail,
        confirmPatientEmail: validEmail,
      },
    });
  };

  const closeCreatePatientModal = () => {
    setCreatePatientModal({
      open: false,
      assignmentId: null,
      requestId: null,
      initialValues: null,
    });
  };

  const openEditPatientModal = async (record) => {
    const patientId = Number(record?.patientId);
    if (!patientId) {
      message.warning(t("elig_warning_no_linked_patient", "Aucun patient lié à cette requête."));
      return;
    }

    setEditPatientModal({
      open: true,
      loading: true,
      patient: null,
      requestId: record?.requestId || null,
    });

    try {
      const res = await axios.get(`${Constants.SERVER_URL}/patient/${patientId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEditPatientModal((prev) => ({
        ...prev,
        loading: false,
        patient: res?.data || null,
      }));
    } catch (error) {
      console.error(error);
      message.error(
        t(
          "elig_error_load_patient_details",
          "Impossible de charger les informations du patient."
        )
      );
      setEditPatientModal({
        open: false,
        loading: false,
        patient: null,
        requestId: null,
      });
    }
  };

  const closeEditPatientModal = () => {
    setEditPatientModal({
      open: false,
      loading: false,
      patient: null,
      requestId: null,
    });
  };

  const handlePatientCreatedFromRequest = async (createdPatient) => {
    try {
      if (Number(createdPatient?.linkedEligibilityRequestId) > 0) {
        message.success(t("elig_success_patient_created_linked", "Patient créé et relié à la requête."));
        fetchRequests();
        return;
      }

      const assignmentId = Number(createPatientModal.assignmentId);
      const patientId = Number(createdPatient?.id);
      if (!assignmentId || !patientId) return;
      await axios.patch(
        `${Constants.SERVER_URL}/eligibility-request/assignments/${assignmentId}/link-patient`,
        {
          patientId,
          forceMismatch: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success(t("elig_success_patient_created_linked", "Patient créé et relié à la requête."));
      fetchRequests();
    } catch (error) {
      console.error(error);
      message.warning(
        t(
          "elig_warning_patient_created_link_failed",
          "Patient créé, mais la liaison automatique a échoué."
        )
      );
    }
  };

  const openRejectModal = (assignmentId) => {
    setRejectModal({
      open: true,
      assignmentId,
      reason: "",
      loading: false,
    });
  };

  const closeRejectModal = () => {
    setRejectModal({
      open: false,
      assignmentId: null,
      reason: "",
      loading: false,
    });
  };

  const confirmRejectAssignment = async () => {
    if (!rejectModal.assignmentId) return;
    const reasonText = String(rejectModal.reason || "").trim();
    if (!reasonText) {
      message.warning(
        t("elig_warning_reject_comment_required", "Veuillez renseigner un commentaire de refus.")
      );
      return;
    }
    try {
      setRejectModal((prev) => ({ ...prev, loading: true }));
      await axios.patch(
        `${Constants.SERVER_URL}/eligibility-request/assignments/${rejectModal.assignmentId}/reject`,
        { reason: reasonText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success(
        t("elig_success_rejected", "Demande refusée. Le gestionnaire peut réassigner.")
      );
      closeRejectModal();
      fetchRequests();
    } catch (error) {
      const backendMessage = error?.response?.data?.message;
      console.error(error);
      message.error(backendMessage || t("elig_error_reject", "Erreur lors du refus."));
      setRejectModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const managerColumns = [
    { title: t("elig_col_doctor", "Médecin"), dataIndex: "doctorName", width: 210 },
    {
      title: t("elig_col_request_number", "Numéro de requête"),
      width: 180,
      render: (_, record) =>
        Number(record.requestId) > 0 ? `REQ-${String(record.requestId).padStart(6, "0")}` : "-",
    },
    { title: t("elig_col_source_clinic", "Clinique source"), dataIndex: "sourceClinicName", width: 160 },
    {
      title: t("elig_col_assignment", "Assignation"),
      width: 260,
      render: (_, record) => {
        if (!record.latestAssignedKinesiologistName) return "-";
        const statusLabel = statusMeta(record.latestAssignmentStatus, t).label;
        const rejectedReason = String(record.latestAssignmentRejectedReason || "").trim();
        const reasonSuffix =
          record.latestAssignmentStatus === "rejected" && rejectedReason
            ? ` - ${t("elig_assignment_reason_prefix", "Motif")}: ${rejectedReason}`
            : "";
        return `${record.latestAssignedKinesiologistName} (${statusLabel}${reasonSuffix})`;
      },
    },
    {
      title: t("elig_col_global_status", "Statut global"),
      dataIndex: "status",
      width: 140,
      render: (status) => <span style={statusTextStyle(status)}>{statusMeta(status, t).label}</span>,
    },
    {
      title: t("elig_col_action", "Action"),
      key: "actions",
      width: 260,
      fixed: "right",
      render: (_, record) => {
        const hasActivePendingAssignment =
          Boolean(record.canCancelAssignment) && Boolean(record.assignmentId);

        return (
        <Space wrap size={[8, 8]}>
          {/* Prevent double assignment in parallel:
              if a pending assignment exists, show only "cancel assignment". */}
          {record.canAssign && !hasActivePendingAssignment && (
            <Button type="primary" onClick={() => openAssignModal(record.dispatchId)}>
              {t("elig_action_assign", "Assigner")}
            </Button>
          )}
          {record.canCancelAssignment && record.assignmentId && (
            <Button danger onClick={() => cancelManagerAssignment(record.assignmentId)}>
              {t("elig_action_cancel_assignment", "Annuler assignation")}
            </Button>
          )}
        </Space>
      );
      },
    },
  ];

  const kinesiologistColumns = [
    { title: t("elig_col_doctor", "Médecin"), dataIndex: "doctorName", width: 210 },
    {
      title: t("elig_col_patient", "Patient"),
      width: 170,
      render: (_, record) => {
        const patientName =
          `${record.patientFirstname || ""} ${record.patientLastname || ""}`.trim() || "-";
        const patientAction = getPatientAction(record);
        let patientActionButton = null;

        if (patientAction === "create") {
          patientActionButton = (
            <Tooltip title={t("elig_tooltip_create_patient_from_request", "Créer un patient à partir de cette requête")}>
              <Button
                type="text"
                icon={<UserAddOutlined />}
                onClick={() => openCreatePatientModal(record)}
                aria-label={t("elig_aria_create_patient", "Créer patient")}
              />
            </Tooltip>
          );
        }

        if (patientAction === "edit") {
          patientActionButton = (
            <Tooltip title={t("elig_tooltip_edit_patient_info", "Modifier les informations du patient")}>
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => openEditPatientModal(record)}
                aria-label={t("elig_aria_edit_patient", "Modifier patient")}
              />
            </Tooltip>
          );
        }

        return (
          <Space size={8}>
            <span>{patientName}</span>
            {patientActionButton}
          </Space>
        );
      },
    },
    {
      title: t("elig_col_contact", "Contact"),
      key: "contact",
      width: 180,
      render: (_, record) => record.phoneNumber || record.email || "-",
    },
    { title: t("elig_col_source_clinic", "Clinique source"), dataIndex: "sourceClinicName", width: 160 },
    { title: t("elig_col_comment", "Commentaire"), dataIndex: "comment", width: 230 },
    {
      title: t("elig_col_status", "Statut"),
      dataIndex: "status",
      width: 130,
      render: (status) => <span style={statusTextStyle(status)}>{statusMeta(status, t).label}</span>,
    },
    {
      title: t("elig_col_actions", "Actions"),
      key: "actions",
      width: 220,
      fixed: "right",
      render: (_, record) =>
        record.canAccept || record.canReject ? (
          <Space wrap size={[8, 8]}>
            {record.canAccept && (
              <Tooltip title={t("elig_tooltip_accept_request", "Accepter la requête")}>
                <span>
                  <Button
                    onClick={() => acceptAssignment(record.assignmentId)}
                    style={{ background: "#52c41a", borderColor: "#52c41a", color: "#fff" }}
                  >
                    {t("elig_action_accept", "Accepter")}
                  </Button>
                </span>
              </Tooltip>
            )}
            {record.canReject && (
              <Button danger onClick={() => openRejectModal(record.assignmentId)}>
                {t("elig_action_reject", "Refuser")}
              </Button>
            )}
          </Space>
        ) : null,
    },
  ];

  return (
    <div style={containerStyle}>
      <div style={headerCardStyle}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            {isManagerView
              ? t("elig_title_manager", "Demandes d'éligibilité reçues (gestionnaire)")
              : t("elig_title_kinesiologist", "Demandes d'éligibilité reçues (kinésiologue)")}
          </Title>
          <div style={chipsStyle}>
            <span style={chipStyle("#e5e7eb")}>
              {t("elig_summary_total", "Total")}: {summary.total}
            </span>
            <span style={chipStyle("#dbeafe", "#1677ff")}>
              {t("elig_summary_pending", "En attente")}: {summary.pending}
            </span>
            <span style={chipStyle("#dcfce7", "#166534")}>
              {t("elig_summary_accepted", "Acceptées")}: {summary.accepted}
            </span>
            <span style={chipStyle("#fee2e2", "#b91c1c")}>
              {t("elig_summary_rejected", "Refusées")}: {summary.rejected}
            </span>
          </div>
        </div>
        <Button type="primary" onClick={fetchRequests}>
          {t("elig_button_refresh", "Actualiser")}
        </Button>
      </div>

      <Table
        dataSource={rows}
        columns={isManagerView ? managerColumns : kinesiologistColumns}
        rowKey={(record) =>
          String(record.assignmentId || record.dispatchId || record.requestId || Math.random())
        }
        loading={loading}
        pagination={{ pageSize: 8, showSizeChanger: false }}
        scroll={{ x: "max-content" }}
      />

      <Modal
        open={assignModal.open}
        title={t("elig_modal_assign_title", "Assigner à un kinésiologue")}
        onCancel={closeAssignModal}
        onOk={() => confirmAssign(false)}
        okText={t("elig_modal_assign_ok", "Assigner")}
        confirmLoading={assignModal.loading}
      >
        <Select
          style={{ width: "100%" }}
          placeholder={t("elig_modal_assign_select_placeholder", "Sélectionner un kinésiologue")}
          options={assignModal.kinesiologists}
          loading={assignModal.optionsLoading}
          value={assignModal.selectedKinesiologistId}
          onChange={(value) =>
            setAssignModal((prev) => ({ ...prev, selectedKinesiologistId: value }))
          }
          showSearch
          optionFilterProp="label"
        />
      </Modal>

      <Modal
        open={rejectModal.open}
        title={t("elig_modal_reject_title", "Refuser cette requête")}
        onCancel={closeRejectModal}
        onOk={confirmRejectAssignment}
        okText={t("elig_modal_reject_ok", "Refuser")}
        okButtonProps={{ danger: true }}
        confirmLoading={rejectModal.loading}
      >
        <Input.TextArea
          rows={4}
          placeholder={t("elig_modal_reject_placeholder", "Commentaire de refus (obligatoire)")}
          value={rejectModal.reason}
          onChange={(e) =>
            setRejectModal((prev) => ({ ...prev, reason: e.target.value }))
          }
        />
      </Modal>

      <Modal
        open={createPatientModal.open}
        title={t("elig_modal_create_patient_title", "Créer un patient depuis la requête")}
        onCancel={closeCreatePatientModal}
        footer={null}
        width={980}
        destroyOnClose
      >
        <CreatePatient
          refetchPatients={() => {}}
          onClose={closeCreatePatientModal}
          initialValues={createPatientModal.initialValues || undefined}
          onPatientCreated={handlePatientCreatedFromRequest}
          eligibilityAssignmentId={createPatientModal.assignmentId || undefined}
          eligibilityRequestDisplayId={
            createPatientModal.requestId
              ? formatRequestId(createPatientModal.requestId)
              : undefined
          }
        />
      </Modal>

      <Modal
        open={editPatientModal.open}
        title={
          editPatientModal.requestId
            ? `${t("elig_modal_edit_patient_title_prefix", "Modifier patient")} - ${formatRequestId(
                editPatientModal.requestId
              )}`
            : t(
                "elig_modal_edit_patient_title_default",
                "Modifier les informations du patient"
              )
        }
        onCancel={closeEditPatientModal}
        footer={null}
        width={980}
        destroyOnClose
      >
        {editPatientModal.loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <Spin />
          </div>
        ) : editPatientModal.patient ? (
          <PatientDetails
            patient={editPatientModal.patient}
            onClose={closeEditPatientModal}
            refetchPatients={fetchRequests}
            hideStatus
          />
        ) : null}
      </Modal>
    </div>
  );
};

export default EligibilityRequestList;
